import { Typography } from "@mui/material";
import { saveAs } from "file-saver";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import CustomButton from "../../../components/CustomButton";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import {
  clearStockTransHistory,
  getTransactionHistoryByStockId,
} from "../../../redux/reducers/clinicSlice";
import { AppColors } from "../../../util/AppColors";
import { transactionTypeList } from "../../../util/dropList";

const tblHdrs = [
  "date",
  "tranType",
  "customerSupplier",
  "invoiceNumber",
  "batchNumber",
  // "openingQty",
  "qtySoldPurchased",
  // "closingQty",
  "mrp",
];

const excelApiColumns = [
  { key: "date", label: "Date" },
  { key: "type", label: "Type" },
  { key: "supplierName", label: "Supplier Name" },
  { key: "userName", label: "User Name" },
  { key: "petName", label: "Pet Name" },
  { key: "invoiceName", label: "Invoice Name" },
  { key: "invoiceNumber", label: "Invoice Number" },
  { key: "quantity", label: "Quantity" },
  { key: "uom", label: "UOM" },
  { key: "landedCost", label: "Landed Cost" },
  { key: "discount", label: "Discount" },
  { key: "itemTotal", label: "Item Total" },
  { key: "totalAmount", label: "Total Amount" },
  { key: "cgst", label: "CGST" },
  { key: "sgst", label: "SGST" },
  { key: "checkOut", label: "Check Out" },
];

const initSearch = {
  startDate: null,
  endDate: null,
  type: "all",
  supplierName: "",
  customerName: "",
  petName: "",
  locationId: "all",
};

const TransactionHistory = ({
  selectedStock = null,
  initialStartDate = null,
  initialEndDate = null,
  initType = null,
}) => {
  const dispatch = useDispatch();
  const [searchValues, setSearchValues] = useState({
    ...initSearch,
    ...(initialStartDate ? { startDate: initialStartDate } : {}),
    ...(initialEndDate ? { endDate: initialEndDate } : {}),
    ...(initType ? { type: initType } : {}),
  });
  const { stockTransHistory } = useSelector((state) => state?.clinic);
  const tranData = stockTransHistory?.data ?? {};
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { locations } = useMemo(() => {
    if (!mandateDataRaw) {
      return { locations: [] };
    }
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        locations: Array.isArray(reqData?.locations) ? reqData.locations : [],
      };
    } catch {
      return { locations: [] };
    }
  }, [mandateDataRaw]);

  const knownLocationIdsRef = useRef([]);
  const locationStockIdRef = useRef(selectedStock?.stockId);
  const locationFilterList = useMemo(() => {
    const ids = new Set(knownLocationIdsRef.current.map((id) => String(id)));
    (tranData?.transactions ?? []).forEach((li) => {
      const locationId = li?.locationId ?? li?.toLocationId;
      if (locationId != null && locationId !== "") ids.add(String(locationId));
    });
    knownLocationIdsRef.current = [...ids];
    return [...ids]
      .map((locationId) => {
        const loc = locations?.find(
          (li) => String(li?.id) === String(locationId),
        );
        if (!loc?.locationName) return null;
        return {
          name: loc.locationName,
          value: String(locationId),
        };
      })
      .filter(Boolean);
  }, [tranData?.transactions, locations]);

  useEffect(() => {
    if (
      String(locationStockIdRef.current ?? "") !==
      String(selectedStock?.stockId ?? "")
    ) {
      locationStockIdRef.current = selectedStock?.stockId;
      knownLocationIdsRef.current = [];
    }
    dispatch(clearStockTransHistory());

    const timeoutId = setTimeout(() => {
      onApiCall();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    selectedStock?.stockId,
    searchValues?.startDate,
    searchValues?.endDate,
    searchValues?.type,
    searchValues?.supplierName,
    searchValues?.customerName,
    searchValues?.petName,
    searchValues?.locationId,
  ]);

  const getResolvedDateRange = () => {
    const startRaw = searchValues?.startDate;
    const endRaw = searchValues?.endDate;
    if (!startRaw && !endRaw) return { startDate: null, endDate: null };

    const start = startRaw ? moment(new Date(startRaw)) : null;
    let end = endRaw ? moment(new Date(endRaw)) : null;

    if (start && !end) end = start.clone();
    if (!start && end) {
      return {
        startDate: end.format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
      };
    }
    if (start && end && end.isBefore(start, "day")) {
      return {
        startDate: end.format("YYYY-MM-DD"),
        endDate: start.format("YYYY-MM-DD"),
      };
    }

    return {
      startDate: start?.format("YYYY-MM-DD") ?? null,
      endDate: end?.format("YYYY-MM-DD") ?? null,
    };
  };

  const onApiCall = (reset = false) => {
    if (!selectedStock?.stockId) return;
    const params = new URLSearchParams();
    if (!reset) {
      const { startDate, endDate } = getResolvedDateRange();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (searchValues?.type) params.append("type", searchValues.type);
      if (searchValues?.supplierName?.length > 0) {
        params.append("supplierName", searchValues.supplierName);
      }
      if (searchValues?.customerName?.length > 0) {
        params.append("userName", searchValues.customerName);
      }
      if (searchValues?.petName?.length > 0) {
        params.append("petName", searchValues.petName);
      }
      if (searchValues?.locationId !== "all" && searchValues?.locationId) {
        params.append("toLocationId", searchValues.locationId);
      }
    }
    dispatch(
      getTransactionHistoryByStockId({
        id: selectedStock?.stockId,
        url: `?${params.toString()}`,
      }),
    );
  };

  const onReset = () => {
    flushSync(() => setSearchValues(initSearch));
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({
      ...searchValues,
      [name?.trim()?.replace(/,$/, "")]: value, // remove spaces & trailing commas
    });
  };

  const getFilteredTransactions = () => {
    return tranData?.transactions ?? [];
    // if (!searchValues?.locationId || searchValues.locationId === "all") {
    //   return transactions;
    // }
    // return transactions.filter(
    //   (li) => String(li?.locationId) === String(searchValues.locationId),
    // );
  };

  const filteredRecordCount = getFilteredTransactions().length;

  const getTableList = () => {
    return getFilteredTransactions()?.map((li) => {
      let reqQty = Number(li?.quantity ?? 0);
      if (li?.type?.toLowerCase() === "sale") reqQty = -reqQty;
      const closingQty = Number(li?.OpeningQuantity ?? 0) + Number(reqQty);
      return {
        ...li,
        closingQty,
        openingQty: li?.OpeningQuantity ?? 0,
        tranType: li?.type,
        date: moment(new Date(li?.date)).format("DD MMM YYYY"),
        customerSupplier: li?.userName ?? li?.supplierName ?? "",
        qtySoldPurchased: `${li?.quantity}${li?.uom?.length > 0 ? ` ${li?.uom}` : ""}`,
        batchNumber: `${li?.batchNo?.length > 0 ? ` ${li?.batchNo}` : ""}`,
        mrp: `${li?.unitSellingPrice ? ` ${li?.unitSellingPrice}` : ""}`,
      };
    });
  };

  const onDownloadReport = () => {
    // Excel source = already loaded API payload in tranData (no export API)
    // Top: tradeName, productName, currentStockQty, totalPurchaseQty, totalSalesQty
    // Body: transactions[]
    const transactions = getFilteredTransactions() ?? [];
    if (!transactions.length) return;

    const { startDate, endDate } = getResolvedDateRange();
    const dateRange =
      startDate || endDate ? `${startDate ?? "-"} to ${endDate ?? "-"}` : "-";

    const summaryRows = [
      [
        `Stock Transaction History - ${tranData?.tradeName ?? ""} / ${
          tranData?.productName ?? ""
        }`,
      ],
      ["Current Stock Qty", tranData?.currentStockQty ?? ""],
      ["Total Purchase Qty", tranData?.totalPurchaseQty ?? ""],
      ["Total Sales Qty", tranData?.totalSalesQty ?? ""],
      ["Total Records", transactions.length],
      ["Date Range", dateRange],
      [],
      excelApiColumns.map((col) => col.label),
    ];

    const dataRows = transactions.map((li) => [
      li?.date ?? "",
      li?.type ?? "",
      li?.supplierName ?? "",
      li?.userName ?? "",
      li?.petName ?? "",
      li?.invoiceName ?? tranData?.tradeName ?? "",
      li?.invoiceNumber ?? "",
      li?.quantity ?? "",
      li?.uom ?? "",
      li?.landedCost ?? "",
      li?.discount ?? "",
      li?.itemTotal ?? "",
      li?.totalAmount ?? li?.total ?? "",
      li?.cgst ?? li?.CGST ?? "",
      li?.sgst ?? li?.SGST ?? "",
      li?.checkOut ?? li?.checkout ?? "",
    ]);

    const sheetRows = [...summaryRows, ...dataRows];
    const worksheet = XLSX.utils.aoa_to_sheet(sheetRows);

    // Auto width by content length for each column
    const colCount = excelApiColumns.length;
    worksheet["!cols"] = Array.from({ length: colCount }, (_, colIdx) => {
      let maxLen = String(excelApiColumns[colIdx]?.label ?? "").length;
      dataRows.forEach((row) => {
        const cellLen = String(row?.[colIdx] ?? "").length;
        if (cellLen > maxLen) maxLen = cellLen;
      });
      // Summary label column also needs space
      if (colIdx === 0) {
        summaryRows.forEach((row) => {
          const cellLen = String(row?.[0] ?? "").length;
          if (cellLen > maxLen) maxLen = cellLen;
        });
      }
      return { wch: Math.min(Math.max(maxLen + 2, 10), 40) };
    });

    const boldStyle = { font: { bold: true } };
    // Title row (A1)
    if (worksheet.A1) {
      worksheet.A1.s = boldStyle;
    }
    // Column header row (row 8 in 1-based Excel = index 7)
    const headerRowIndex = summaryRows.length; // 1-based Excel row of headers
    excelApiColumns.forEach((_, colIdx) => {
      const cellRef = XLSX.utils.encode_cell({
        r: headerRowIndex - 1,
        c: colIdx,
      });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = boldStyle;
      }
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transaction History");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const productLabel = [tranData?.tradeName, tranData?.productName]
      .filter(Boolean)
      .join("_")
      .replace(/[/\\?%*:|"<>]/g, "-")
      .trim();
    saveAs(
      new Blob([excelBuffer], { type: "application/octet-stream" }),
      `Stock_Transaction_History${productLabel ? `_${productLabel}` : ""}.xlsx`,
    );
  };

  return (
    <>
      {/* <TopBar> */}
      <div className="mt20">
        <div className="flex-row">
          <div className="top-row-cen-con w100Per">
            <div className="w13Per">
              <CustomTextField
                placeholder="Start Date"
                fullWidth
                name="startDate"
                handleChange={onSearchChange}
                value={searchValues?.startDate}
                backgroundColor={AppColors.lightBlue2}
                label="Start Date"
                mobileDate
              />
            </div>
            <div className="w13Per ml15">
              <CustomTextField
                placeholder="End Date"
                fullWidth
                name="endDate"
                handleChange={onSearchChange}
                value={searchValues?.endDate}
                backgroundColor={AppColors.lightBlue2}
                label="End Date"
                mobileDate
              />
            </div>
            <div className="w13Per ml15">
              <CustomSelect
                list={[
                  ...transactionTypeList,
                  { name: "Transfers", value: "TRANSFER" },
                  { name: "Consumption", value: "consumption" },
                ]}
                fullWidth
                name="type"
                handleChange={onSearchChange}
                value={searchValues?.type}
                backgroundColor={AppColors.lightBlue2}
                label="Type"
                select
              />
            </div>
            <div className="w13Per ml15">
              <CustomTextField
                placeholder="Supplier Name"
                fullWidth
                name="supplierName"
                handleChange={onSearchChange}
                value={searchValues?.supplierName}
                backgroundColor={AppColors.lightBlue2}
                label="Supplier Name"
              />
            </div>
            <div className="w13Per ml15">
              <CustomTextField
                placeholder="Customer Name"
                fullWidth
                name="customerName"
                handleChange={onSearchChange}
                value={searchValues?.customerName}
                backgroundColor={AppColors.lightBlue2}
                label="Customer Name"
              />
            </div>
            <div className="w13Per ml15">
              <CustomTextField
                placeholder="Pet Name"
                fullWidth
                name="petName"
                handleChange={onSearchChange}
                value={searchValues?.petName}
                backgroundColor={AppColors.lightBlue2}
                label="Pet Name"
              />
            </div>
            <div className="w13Per ml15">
              <CustomSelect
                list={[{ name: "All", value: "all" }, ...locationFilterList]}
                fullWidth
                name="locationId"
                handleChange={onSearchChange}
                value={String(searchValues?.locationId ?? "all")}
                backgroundColor={AppColors.lightBlue2}
                label="Location"
                select
              />
            </div>

            <div className="ml15">
              <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
            </div>
            <div className="ml15">
              <CustomButton text="⬇" onClick={onDownloadReport} smallBtn />
            </div>
          </div>
        </div>
        <div className="flex-row-ali-cen mt10 mh30">
          <Typography className="txt-semi-bold fs14">
            {tranData?.tradeName ?? ""}
          </Typography>
          <Typography className="txt-semi-bold fs14 ml15">
            {tranData?.productName ?? ""}
          </Typography>
        </div>
        <div className="flex-row-ali-cen mh30">
          <Typography className="font-medium fs14">
            Current stock Qty: {tranData?.currentStockQty ?? ""}
          </Typography>
          <Typography className="font-medium fs14 ml15">
            Total purchase Qty: {tranData?.totalPurchaseQty ?? ""}
          </Typography>
          <Typography className="font-medium fs14 ml15">
            Total sales Qty: {tranData?.totalSalesQty ?? ""}
          </Typography>
          {/* <Typography className="font-medium fs14 ml15">
            Opening Balance:{" "}
            {tranData?.transactions?.[tranData?.transactions?.length - 1]
              ?.beforeQty ?? "0"}
          </Typography>
          <Typography className="font-medium fs14 ml15">
            Closing Balance: {tranData?.transactions?.[0]?.afterQty ?? "0"}
          </Typography> */}
          {/* <Typography className="font-medium fs14 ml15">
            Opening Qty: {tranData?.totalSalesQty ?? ""}
          </Typography>
          <Typography className="font-medium fs14 ml15">
            Closing Qty: {tranData?.totalSalesQty ?? ""}
          </Typography> */}
        </div>
      </div>
      <Typography className="font-medium fs12 gray28 mt10 mh30">
        Number of records: {filteredRecordCount}
      </Typography>
      <div className="mtMin10">
        <CustomTable
          grey
          columns={tblHdrs}
          datas={getTableList()}
          rowsPerPage={filteredRecordCount || 1}
          totalRecords={filteredRecordCount}
        />
      </div>
    </>
  );
};

export default TransactionHistory;
